import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';
import { LOGOS, BACKGROUNDS } from '../../config/logoConfig';
import { loginUser } from '../../api/apiClient';

const LoginPage = ({ onLogin }) => {
  const [employeeId, setEmployeeId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const navigate = useNavigate();

  // Add fade-in effect on component mount
  useEffect(() => {
    setFadeIn(true);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!employeeId || !password) {
      setError('Please enter both Employee ID and password');
      setShowErrorModal(true);
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Call the login API
      const response = await loginUser(employeeId, password);
      
      // Store user info in localStorage for persistence
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Call the onLogin callback if provided
      if (onLogin) {
        onLogin(response.user);
      } else {
        // Fallback if onLogin prop is not provided
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login failed:', error);
      
      // Check if the error is related to role
      if (error.message.includes('Access denied')) {
        setError('You do not have admin privileges to access this application');
      } else {
        setError(error.message || 'Invalid credentials. Please try again.');
      }
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
  };

  // Inline style with background image
  const backgroundStyle = {
    backgroundImage: `url(${BACKGROUNDS.LOGIN})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };

  return (
    <div className="login-page" style={backgroundStyle}>
      <div className="login-left-overlay">
        <img src={LOGOS.HINDALCO.DEFAULT} alt="Hindalco Logo" className="hindalco-logo" />
        <div className="left-welcome-text">
          <div className="left-welcome-hey">Hey There!</div>
          <div className="left-welcome-title">Welcome</div>
        </div>
      </div>
      <div className={`login-container ${fadeIn ? 'fade-in' : ''}`}>
        <div className="login-form-area">
          <div className="login-logo">
            <img src={LOGOS.DRIVE360.ASSET} alt="Drive360 Logo" className="pulse-on-load" />
          </div>
          
          <h3 className="login-title" style={{ textAlign: 'left',
             marginLeft: '20px',
             marginTop: '4px',
             marginBottom: '0px'
            }}>Admin Login</h3>
          
          <form onSubmit={handleLogin} style={{ marginTop: '10px' }}>
            <div className="form-field">
              <input
                type="text"
                placeholder="Admin ID"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="login-input"
                disabled={isLoading}
              />
            </div>
            
            <div className="form-field">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
                disabled={isLoading}
              />
            </div>
            
            <div className="login-actions">
              <button 
                type="submit" 
                className={`login-button ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="loading-text">Logging in...</span>
                ) : (
                  'Login'
                )}
              </button>
            </div>
          </form>
          
          <div className="login-footer">
            <div style={{ color: '#888', fontSize: 16, marginBottom: 4, textAlign: 'left' }}>Powered by:</div>
            <div className="footer-logo" style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
              <div>
                <img src={LOGOS.EFRAME.DEFAULT} alt="Eframe Infomedia Logo" style={{ height: 48, objectFit: 'contain' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.5, textAlign: 'left', marginLeft: '50px' }}>
                <span style={{ fontWeight: 600, color: '#222', fontSize: 18 }}>Eframe Infomedia Pvt Ltd</span>
                <a
                  className="eframe-link"
                  href="https://www.eframe.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#444', fontSize: 16, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  www.eframe.in
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Modal Popup */}
      {showErrorModal && (
        <div className="error-modal-overlay" onClick={closeErrorModal}>
          <div className="error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="error-modal-header">
              <h3>Authentication Failed</h3>
              <button className="close-modal-button" onClick={closeErrorModal}>&times;</button>
            </div>
            <div className="error-modal-body">
              <div className="error-icon-container">
                <div className="error-icon-circle">
                  <i className="error-icon">⚠</i>
                </div>
              </div>
              <p>{error}</p>
            </div>
            <div className="error-modal-footer">
              <button className="error-modal-button" onClick={closeErrorModal}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage; 